import { useQueryParams } from '@cue/hooks';
import { StyledComponent } from '@cue/theme';
import { CSSObject } from '@emotion/styled';
import { useCookieConsent } from '@project/hooks/use-cookie-consent';
import { useUser } from '@project/hooks/use-user';
import {
  COOKIE_CONSENT_TYPE,
  Consent,
  ConsentTypes,
  OPEN_COOKIE_SETTINGS_EVENT,
  type UsercentricsUi,
} from '@project/site/core/consent/consent';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CookieFallbackDialog } from './cookie-fallback-dialog';

export { COOKIE_CONSENT_TYPE, ConsentTypes };

const UC_EVENT = 'UC_UI_CMP_EVENT';
const UC_CHOICE_EVENTS = ['ACCEPT_ALL', 'DENY_ALL', 'SAVE'];
const UC_INTERACTION_KEY = 'uc_user_interaction';

/**
 * Keeps the consent state in sync: Usercentrics events (or the fallback dialog
 * on localhost) update localStorage, Matomo tracking and the go backend.
 */
export const CookieConsent: React.FC<StyledComponent<CookieConsentCSSOverride>> = () => {
  const location = useLocation();
  const [params, setParams] = useQueryParams({ navigate, location });
  const [, setConsentInGo] = useCookieConsent();
  const { isAuthenticated } = useUser();
  const [fallbackOpen, setFallbackOpen] = useState(false);

  const persist = useCallback(
    (type: ConsentTypes) => {
      const isFirstChoice = Consent.getStoredType() === null;
      Consent.save(type);
      // anonymous users only sync their first choice, logged in users every change
      if (isAuthenticated || isFirstChoice) {
        setConsentInGo({ consent: type === ConsentTypes.ALL });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated],
  );
  const persistRef = useRef(persist);
  persistRef.current = persist;

  // the auth status is needed to sync the consent to go
  const authKnown = isAuthenticated !== undefined;

  useEffect(() => {
    if (!authKnown) return;
    let removeUcListener: (() => void) | undefined;
    let cancelled = false;

    const bindUsercentrics = (uc: UsercentricsUi) => {
      if (!uc.isInitialized()) return;
      const typeFromUc = () =>
        uc.areAllConsentsAccepted() ? ConsentTypes.ALL : ConsentTypes.REQUIRED;

      // restore a choice made on an earlier visit; nothing is stored before the user chose
      // written by Usercentrics itself, so it is not a LocalStorageUtil value
      // eslint-disable-next-line cuenect-web-rules/no-direct-use-of-local-storage
      if (localStorage.getItem(UC_INTERACTION_KEY) === 'true') {
        persistRef.current(typeFromUc());
      }

      const onUcEvent = (e: Event) => {
        const { type } = (e as CustomEvent<{ type: string }>).detail;
        if (UC_CHOICE_EVENTS.includes(type)) persistRef.current(typeFromUc());
      };
      window.addEventListener(UC_EVENT, onUcEvent);
      removeUcListener = () => window.removeEventListener(UC_EVENT, onUcEvent);
    };

    Consent.waitForUc().then((uc) => {
      if (cancelled) return;
      if (uc) {
        bindUsercentrics(uc);
        return;
      }
      if (!Consent.isLocalhost()) return;

      const stored = Consent.getStoredType();
      if (stored) {
        Consent.applyTracking(stored === ConsentTypes.ALL);
      } else {
        setFallbackOpen(true);
      }
    });

    return () => {
      cancelled = true;
      removeUcListener?.();
    };
  }, [authKnown]);

  useEffect(() => {
    const open = () => setFallbackOpen(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, []);

  // deep link used by the cookie policy page
  useEffect(() => {
    if (params[COOKIE_CONSENT_TYPE] !== 'open') return;
    Consent.openPreferences();
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { [COOKIE_CONSENT_TYPE]: _open, ...restOfParams } = params;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    setParams(restOfParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <CookieFallbackDialog
      open={fallbackOpen}
      onChoose={(type) => {
        persist(type);
        setFallbackOpen(false);
      }}
    />
  );
};

export type CookieConsentCSSOverride = {
  '.cue-project-cookie-consent'?: CSSObject;
  '.cue-project-cookie-consent-buttons'?: CSSObject;
};
